import React, { ReactElement } from 'react';
import {
  ChartsEngine,
  ChartsEngineEvents,
  EngineOptions,
  LineChartSeriesOptions,
} from './ChartsEngine';

interface LineChartProps {
  lines?: LineChartOptions[];
  tooltipOptions?: LineChartTooltipOptions;
  legendTop?: number | string;
  legendLeft?: number | string;
  gridTop?: number | string;
  gridBottom?: number | string;
  onChartClick?: (params: unknown) => void;
  tooltipFormatter?: (params: unknown) => string;
}

interface LineChartTooltipOptions {
  valueFormatter: (value: string | number) => string;
}

interface LineChartOptions {
  name: string;
  points: LineChartPoint[];
  color?: ColorHex;
  dashed?: boolean;
  showPoint?: boolean;
  width?: number;
}

interface LineChartPoint {
  x: string | number;
  y: string | number | null;
}

function LineChart({
  lines,
  tooltipOptions,
  legendTop,
  legendLeft,
  gridTop,
  gridBottom,
  onChartClick,
  tooltipFormatter,
}: LineChartProps): ReactElement {
  const categories = lines && lines?.length > 0 ? lines[0].points.map(point => point.x) : [];

  const series: LineChartSeriesOptions[] | undefined = lines?.map(chartOptions => {
    return {
      name: chartOptions.name,
      data: chartOptions.points.map(point => point.y),
      type: 'line',
      smooth: 0.2,
      connectNulls: true,
      lineStyle: {
        type: chartOptions.dashed ? 'dashed' : 'solid',
        width: chartOptions.width ?? 1,
      },
      color: chartOptions.color,
      showSymbol: chartOptions.showPoint,
      emphasis: {
        focus: 'self',
      },
    };
  });

  const legendType = series ? (series.length > 30 ? 'scroll' : 'plain') : 'plain';
  const defaultGridBottom = series ? (series.length > 30 ? 50 : 25 + (series.length / 5) * 20) : 50;

  const option: EngineOptions = {
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: categories,
    },
    yAxis: {
      type: 'value',
      // max: 100,
    },
    dataZoom: {
      type: 'inside',
    },
    legend: {
      orient: 'horizontal',
      // top: '10%',
      type: legendType,
      // top: 500,
      top: legendTop ?? 'bottom',
      left: legendLeft,
      height: 20,
      pageIconSize: 25,
      pageButtonGap: 30,
    },
    tooltip: {
      confine: true,
      trigger: 'axis',
      formatter: tooltipFormatter,
      valueFormatter: value => {
        if (value === undefined) {
          return '-';
        }
        return tooltipOptions ? tooltipOptions.valueFormatter(value as string) : (value as string);
      },
    },
    grid: {
      left: '3%',
      right: '4%',
      top: gridTop,
      bottom: gridBottom ?? defaultGridBottom,
      // bottom: 50,
      containLabel: true,
    },
    series: series,
  };

  const onEvents: ChartsEngineEvents | undefined = onChartClick
    ? {
        click: onChartClick,
      }
    : undefined;

  // const options2 = {
  //   grid: { top: 8, right: 8, bottom: 24, left: 36 },
  //   xAxis: {
  //     type: 'category',
  //     data: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  //   },
  //   yAxis: {
  //     type: 'value',
  //   },
  //   series: [
  //     {
  //       data: [820, 932, 901, 934, 1290, 1330, 1320],
  //       type: 'line',
  //       smooth: true,
  //     },
  //   ],
  //   tooltip: {
  //     trigger: 'axis',
  //   },
  // };

  return <ChartsEngine options={option} onEvents={onEvents} />;
}

export { LineChart, type LineChartPoint, type LineChartOptions };
